import { isNull } from '@mbfab/shared'

// 複数列ソートの状態。先頭が最優先（DataTable の sorters props と共通の型）
export type DataTableSorter = { key: string; order: 'asc' | 'desc' }

// ヘッダークリックのたびに呼ぶ、複数列ソートの次状態計算。1回目=昇順・2回目=降順・3回目=解除で、
// 未ソートの列をクリックすると末尾に追加されるため複数列を跨いだソート（列の重なり）が成立する。
// ミルシート screens/millsheet/filter-state.ts の nextMillsheetSorters と同じ挙動を、
// 列 key の型（MillsheetColumnKey 等の画面固有 union）に依存しない形へ汎用化したもの
export function nextSorters(current: readonly DataTableSorter[], key: string): DataTableSorter[] {
  const index = current.findIndex((sorter) => sorter.key === key)
  if (index === -1) {
    return [...current, { key, order: 'asc' }]
  }
  const existing = current[index]
  if (isNull(existing)) {
    return [...current]
  }
  if (existing.order === 'asc') {
    return current.map((sorter, i) => (i === index ? { key, order: 'desc' } : sorter))
  }
  return current.filter((_, i) => i !== index)
}

// クライアント全件保持のテーブル（ミルシート・加工証明・未紐付けミルシート）向けのソート実行。
// 先頭の sorter が最優先の複数列ソート。comparator は昇順基準で渡し、降順は符号反転で表現することで
// 呼び出し側が asc/desc それぞれの比較関数を用意する必要をなくす。comparator が無いキーはそのソート条件を
// 無視して次の sorter の比較へ進む（列削除・未対応列を渡してもエラーにしない）。
// 元配列は破壊しない（[...rows].sort はコピーに対して破壊的ソートを行うため rows 自体は変化しない）
export function applySorters<Row>(
  rows: readonly Row[],
  sorters: readonly DataTableSorter[],
  comparators: Record<string, (a: Row, b: Row) => number>,
): Row[] {
  if (sorters.length === 0) {
    return [...rows]
  }
  return [...rows].sort((a, b) => {
    for (const sorter of sorters) {
      const comparator = comparators[sorter.key]
      if (isNull(comparator)) {
        continue
      }
      const result = comparator(a, b)
      if (result !== 0) {
        return sorter.order === 'asc' ? result : -result
      }
    }
    return 0
  })
}
